import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Avatar, Tooltip, Carousel, Spin } from 'antd';
import {
    CheckCircleFilled,
    EnvironmentFilled,
    FacebookFilled,
    FireFilled,
    FireOutlined,
    HeartFilled,
    InfoCircleOutlined,
    InstagramFilled,
    LeftOutlined,
    LoadingOutlined,
    PhoneOutlined,
    RightOutlined,
} from '@ant-design/icons';
import Navbar from '../navbar/Navbar';
import Notice from '../notice/Notice';
import { fetchFacilities, fetchShops } from '../services/apiService';
import languages from '../services/languageConfig';
import global from '../assets/global.png';
import direction from '../assets/directions.png';
import { Logo } from '../../constants/commonConstant';
import FacilitySlider from '../facility-slider/FacilitySlider';

const ITEMS_PER_PAGE = 5;

const Home = () => {
    const [logoVisible, setLogoVisible] = useState(true);
    const [shops, setShops] = useState([]);
    const [facilityData, setFacilityData] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [language, setLanguage] = useState('en');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalShops, setTotalShops] = useState(0);
    const languageRef = useRef(language);
    const [hoverStates, setHoverStates] = useState({});

    useEffect(() => {
        const timer = setTimeout(() => setLogoVisible(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const fetchFacilityData = async () => {
            try {
                const facilities = await fetchFacilities();
                setFacilityData(facilities);
            } catch (error) {
                console.error('Error fetching facility data:', error);
            }
        };
        fetchFacilityData();
    }, []);

    const fetchPageData = useCallback(async (page, location = '') => {
        setSearchLoading(true);
        try {
            const { shops: fetchedShops, totalShops: fetchedTotal } = await fetchShops({
                page,
                limit: ITEMS_PER_PAGE,
                search: location,
                language: languageRef.current,
            });
            setShops(fetchedShops);
            setTotalShops(fetchedTotal);
        } catch (error) {
            console.error('Error fetching shops:', error);
        } finally {
            setSearchLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPageData(currentPage, selectedLocation);
    }, [currentPage, selectedLocation, fetchPageData]);

    const handleLanguageChange = (newLanguage) => {
        if (newLanguage !== language) {
            setLanguage(newLanguage);
            languageRef.current = newLanguage;
            setCurrentPage(1);
            setSelectedLocation('');
        }
    };

    const handleSearch = (searchText) => {
        setCurrentPage(1);
        setSelectedLocation(searchText.trim());
    };

    const handlePageChange = (page) => {
        if ((page - 1) * ITEMS_PER_PAGE < totalShops) {
            setCurrentPage(page);
        }
    };

    const handleMouseEnter = (index, type) => {
        setHoverStates((prev) => ({
            ...prev,
            [index]: { ...prev[index], [type]: true },
        }));
    };

    const handleMouseLeave = (index, type) => {
        setHoverStates((prev) => ({
            ...prev,
            [index]: { ...prev[index], [type]: false },
        }));
    };

    const getLastUpdateDate = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleDateString();
    };

    return (
        <div style={{ position: 'relative', minHeight: '100vh' }}>
            {logoVisible && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                        opacity: 1,
                        transition: 'opacity 1s ease-in-out',
                    }}
                >
                    <span
                        style={{
                            fontSize: '60px',
                            color: 'white',
                            fontFamily: 'Bauhaus_93',
                        }}
                    >
                        {Logo}
                    </span>
                </div>
            )}

            {!logoVisible && (
                <>
                    <Navbar
                        onSearch={handleSearch}
                        onLanguageChange={handleLanguageChange}
                        resetPage={() => setCurrentPage(1)}
                    />
                    <Notice language={language} />
                    <div
                        style={{
                            textAlign: 'center',
                            color: 'white',
                            margin: '20px 0 30px',
                            fontSize: languages[language].fontSize,
                            fontFamily: languages[language].fontFamily,
                        }}
                    >
                        {!searchLoading && (
                            <span>
                                {languages[language].restaurantCountText(selectedLocation, totalShops)}{' '}
                                <FireOutlined />
                            </span>
                        )}
                    </div>

                    <div style={{ minHeight: '100vh' }}>
                        {searchLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <Spin
                                    indicator={<LoadingOutlined style={{ fontSize: 38, color: 'white' }} />}
                                />
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    minHeight: '100vh',
                                    gap: '34px',
                                    paddingTop: '12px',
                                    paddingBottom: '60px',
                                }}
                            >
                                {shops.map((shop, index) => (
                                    <div
                                        key={shop.id || index}
                                        style={{
                                            width: '90%',
                                            maxWidth: '500px',
                                            borderRadius: '15px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '15px',
                                            textAlign: 'left',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        {shop.image && Array.isArray(shop.image) && shop.image.length > 0 && (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    borderRadius: '10px',
                                                    overflow: 'hidden',
                                                    position: 'relative',
                                                }}
                                            >
                                                {shop.image.length === 1 ? (
                                                    <div style={{ position: 'relative' }}>
                                                        <img
                                                            src={shop.image[0]}
                                                            alt={`${shop.name}-single`}
                                                            style={{
                                                                width: '100%',
                                                                height: '200px',
                                                                objectFit: 'cover',
                                                                borderRadius: '8px',
                                                            }}
                                                        />
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                bottom: '0',
                                                                left: '0',
                                                                right: '0',
                                                                height: '60px',
                                                                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.0))',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '12px',
                                                                gap: '10px',
                                                            }}
                                                        >
                                                            <Avatar src={shop.avatar} size={50} />
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                                                                    {shop.name}{' '}
                                                                    {shop.isVerified && (
                                                                        <Tooltip title="Verified">
                                                                            <CheckCircleFilled style={{ color: '#007bfc', fontSize: '12px', cursor: 'pointer' }} />
                                                                        </Tooltip>
                                                                    )}
                                                                    {' '}
                                                                    {shop.isTrending && (
                                                                        <Tooltip title="Trending">
                                                                            <FireFilled style={{ color: '#FFA500', fontSize: '12px', cursor: 'pointer' }} />
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: languages[language].locationFontSize, color: '#bbb', fontFamily: languages[language].fontFamily, }}>
                                                                    <EnvironmentFilled style={{ fontSize: '12px', color: '#bbb' }} />
                                                                    {shop[`location_${language}`]}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Carousel
                                                        arrows
                                                        infinite={false}
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: '500px',
                                                            margin: '0 auto',
                                                        }}
                                                    >
                                                        {shop.image.map((imgSrc, imgIndex) => (
                                                            <div key={imgIndex} style={{ position: 'relative' }}>
                                                                <img
                                                                    src={imgSrc}
                                                                    alt={`${shop.name}-${imgIndex}`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '200px',
                                                                        objectFit: 'cover',
                                                                        borderRadius: '8px',
                                                                    }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        position: 'absolute',
                                                                        bottom: '0',
                                                                        left: '0',
                                                                        right: '0',
                                                                        height: '60px',
                                                                        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.0))',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        padding: '12px',
                                                                        gap: '10px',
                                                                        zIndex: 1,
                                                                    }}
                                                                >
                                                                    <Avatar src={shop.avatar} size={50} />
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                                                                            {shop.name}{' '}
                                                                            {shop.isVerified && (
                                                                                <Tooltip title="Verified">
                                                                                    <CheckCircleFilled style={{ color: '#007bfc', fontSize: '12px', cursor: 'pointer' }} />
                                                                                </Tooltip>
                                                                            )}
                                                                            {' '}
                                                                            {shop.isTrending && (
                                                                                <Tooltip title="Trending">
                                                                                    <FireFilled style={{ color: '#FFA500', fontSize: '12px', cursor: 'pointer' }} />
                                                                                </Tooltip>
                                                                            )}
                                                                        </div>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: languages[language].locationFontSize, color: '#bbb', fontFamily: languages[language].fontFamily, }}>
                                                                            <EnvironmentFilled style={{ fontSize: '12px', color: '#bbb' }} />
                                                                            {shop[`location_${language}`]}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </Carousel>
                                                )}
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                marginBottom: '6px',
                                            }}
                                        >
                                            {/* Website Icon */}
                                            {shop.website && (
                                                <div
                                                    style={{
                                                        display: 'inline-flex',
                                                        fontFamily: languages[language].fontFamily,
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '85px',
                                                        height: '32px',
                                                        borderRadius: '20px',
                                                        border: '2px solid white',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        padding: '0 8px',
                                                        boxSizing: 'border-box',
                                                        backgroundColor: hoverStates[index]?.website ? 'white' : 'transparent',
                                                        transition: 'background-color 0.3s ease, color 0.3s ease',
                                                    }}
                                                    onClick={() => window.open(`${shop.website}`, '_blank')}
                                                    onMouseEnter={() => handleMouseEnter(index, 'website')}
                                                    onMouseLeave={() => handleMouseLeave(index, 'website')}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '24px',
                                                            height: '24px',
                                                        }}
                                                    >
                                                        <img
                                                            src={global}
                                                            alt="website"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain',
                                                                filter: hoverStates[index]?.website ? 'invert(1)' : 'none',
                                                                transition: 'filter 0.3s ease',
                                                            }}
                                                        />
                                                    </div>
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                            color: hoverStates[index]?.website ? 'black' : 'white',
                                                            marginLeft: '6px',
                                                            transition: 'color 0.3s ease',
                                                        }}
                                                    >
                                                        {languages[language].website}
                                                    </span>
                                                </div>
                                            )}


                                            {shop.directions && (
                                                <div
                                                    style={{
                                                        display: 'inline-flex',
                                                        fontFamily: languages[language].fontFamily,
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '95px',
                                                        height: '32px',
                                                        borderRadius: '20px',
                                                        border: '2px solid white',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        padding: '0 8px',
                                                        boxSizing: 'border-box',
                                                        backgroundColor: hoverStates[index]?.directions ? 'white' : 'transparent',
                                                        transition: 'background-color 0.3s ease, color 0.3s ease',
                                                    }}
                                                    onClick={() => window.open(`${shop.directions}`, '_blank')}
                                                    onMouseEnter={() => handleMouseEnter(index, 'directions')}
                                                    onMouseLeave={() => handleMouseLeave(index, 'directions')}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '24px',
                                                            height: '24px',
                                                        }}
                                                    >
                                                        <img
                                                            src={direction}
                                                            alt="directions"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain',
                                                                filter: hoverStates[index]?.directions ? 'invert(1)' : 'none',
                                                                transition: 'filter 0.3s ease',
                                                            }}
                                                        />
                                                    </div>
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                            color: hoverStates[index]?.directions ? 'black' : 'white',
                                                            marginLeft: '6px',
                                                            transition: 'color 0.3s ease',
                                                        }}
                                                    >
                                                        {languages[language].directions}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Phone Icon */}
                                            {shop.phone && (
                                                <div
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        fontFamily: languages[language].fontFamily,
                                                        justifyContent: 'center',
                                                        width: '80px',
                                                        height: '32px',
                                                        borderRadius: '20px',
                                                        border: '2px solid white',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        padding: '0 8px',
                                                        boxSizing: 'border-box',
                                                        backgroundColor: hoverStates[index]?.phone ? 'white' : 'transparent',
                                                        transition: 'background-color 0.3s ease, color 0.3s ease',
                                                    }}
                                                    onClick={() => shop.phone && window.open(`tel:${shop.phone}`, '_self')}
                                                    onMouseEnter={() => handleMouseEnter(index, 'phone')}
                                                    onMouseLeave={() => handleMouseLeave(index, 'phone')}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '24px',
                                                            height: '24px',
                                                            transform: 'scaleX(-1)',
                                                        }}
                                                    >
                                                        <PhoneOutlined
                                                            style={{
                                                                fontSize: '16px',
                                                                color: hoverStates[index]?.phone ? 'black' : 'white',
                                                                transition: 'color 0.3s ease',
                                                            }}
                                                        />
                                                    </div>
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                            color: hoverStates[index]?.phone ? 'black' : 'white',
                                                            marginLeft: '6px',
                                                            transition: 'color 0.3s ease',
                                                        }}
                                                    >
                                                        {languages[language].phone}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Info Icon */}
                                            {shop.updatedAt && (
                                                <Tooltip title={`This post was updated on ${getLastUpdateDate(shop.updatedAt)}`}>
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '24px',
                                                            height: '24px',
                                                            transform: 'scaleX(-1)',
                                                        }}
                                                    >
                                                        <InfoCircleOutlined
                                                            style={{
                                                                fontSize: '16px',
                                                                color: hoverStates[index]?.lastUpdate ? 'black' : 'white',
                                                                transition: 'color 0.3s ease',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            )}

                                        </div>

                                        {shop.facilities && shop.facilities.length > 0 && (
                                            <FacilitySlider facilities={shop.facilities} facilityData={facilityData} language={language} />
                                        )}
                                        <div style={{ fontFamily: languages[language].fontFamily, fontSize: languages[language].fontSize, color: 'white', marginBottom: '10px' }}>
                                            {shop[`description_${language}`]}
                                        </div>
                                        <div>
                                            {shop.hashtags && shop.hashtags.length > 0 && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '5px' }}>
                                                    {shop.hashtags.map((hashtag, index) => (
                                                        <span
                                                            key={hashtag}
                                                            style={{
                                                                color: '#ccc',
                                                                cursor: 'pointer',
                                                                fontSize: '14px',
                                                            }}
                                                        >
                                                            {hashtag}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                <a href={shop.facebook} target="_blank" rel="noopener noreferrer">
                                                    <FacebookFilled
                                                        style={{
                                                            fontSize: '20px',
                                                            color: 'white',
                                                        }}
                                                    />
                                                </a>
                                                <a href={shop.instagram} target="_blank" rel="noopener noreferrer">
                                                    <InstagramFilled
                                                        style={{
                                                            fontSize: '21px',
                                                            color: 'white',
                                                        }}
                                                    />
                                                </a>
                                                <span style={{ marginLeft: '5px', color: 'white', fontSize: '14px' }}>/ @follow us on social media
                                                    <> </><HeartFilled style={{ color: '#ff3040' }} />
                                                </span>
                                            </div>
                                        </div>
                                        {index < shops.length - 1 && (
                                            <hr
                                                style={{
                                                    width: '100%',
                                                    border: 'none',
                                                    height: '0.2px',
                                                    backgroundColor: 'gray',
                                                    margin: '10px 0',
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {!searchLoading && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '48px',
                                width: 'calc(100% - 32px)',
                                maxWidth: '500px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                        >
                            {/* Previous Button */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    width: '30px',
                                    height: '30px',
                                    backgroundColor: currentPage === 1 ? '#f0f0f0' : 'white',
                                    color: currentPage === 1 ? '#bbb' : 'black',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.3s ease, color 0.3s ease',
                                }}
                                onClick={() => {
                                    if (currentPage > 1) handlePageChange(currentPage - 1);
                                }}
                                onMouseEnter={(e) => {
                                    if (currentPage > 1) e.currentTarget.style.backgroundColor = '#f0f0f0';
                                }}
                                onMouseLeave={(e) => {
                                    if (currentPage > 1) e.currentTarget.style.backgroundColor = 'white';
                                }}
                            >
                                <LeftOutlined style={{ fontSize: '12px' }} />
                            </div>

                            {/* Current Page */}
                            <div
                                style={{
                                    fontFamily: languages[language].fontFamily,
                                    fontSize: languages[language].fontSize,
                                    color: 'white',
                                }}
                            >
                                {languages[language].page} {currentPage}
                            </div>

                            {/* Next Button */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    width: '30px',
                                    height: '30px',
                                    backgroundColor: currentPage * ITEMS_PER_PAGE >= totalShops ? '#f0f0f0' : 'white',
                                    color: currentPage * ITEMS_PER_PAGE >= totalShops ? '#bbb' : 'black',
                                    cursor: currentPage * ITEMS_PER_PAGE >= totalShops ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.3s ease, color 0.3s ease',
                                }}
                                onClick={() => {
                                    if (currentPage * ITEMS_PER_PAGE < totalShops) handlePageChange(currentPage + 1);
                                }}
                                onMouseEnter={(e) => {
                                    if (currentPage * ITEMS_PER_PAGE < totalShops) e.currentTarget.style.backgroundColor = '#f0f0f0';
                                }}
                                onMouseLeave={(e) => {
                                    if (currentPage * ITEMS_PER_PAGE < totalShops) e.currentTarget.style.backgroundColor = 'white';
                                }}
                            >
                                <RightOutlined style={{ fontSize: '12px' }} />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Home;
